import React, { useState, useEffect, useRef } from 'react'
import {
  Plus,
  MessageSquareText,
  AlertTriangle,
  CheckCircle,
  Clock,
  User,
  Tag,
  Building,
  Download
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import { Breadcrumb } from '../../components/Common'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import supportTicketService from '../../services/supportTicketService'
import '../../components/ConfirmationModal/ConfirmationModal.css'

const STATUS_OPTIONS = ['Open', 'Assigned', 'In Progress', 'Waiting for Customer', 'In Review', 'Resolved', 'Closed', 'Inactive']
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical']
const TYPE_OPTIONS = ['Bug', 'Feature', 'Question', 'Incident', 'Task']

const priorityClass = (p) => {
  if (p === 'Critical') return 'critical'
  if (p === 'High') return 'high'
  if (p === 'Medium') return 'medium'
  return 'low'
}

const SupportTicketList = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalTickets: 0,
    openTickets: 0,
    inProgressTickets: 0,
    resolvedTickets: 0,
    criticalTickets: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [filters, setFilters] = useState({ status: '', priority: '', type: '' })
  const hasLoadedRef = useRef(false)
  const hasShownEmptyRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      loadData()
      hasLoadedRef.current = true
    }
  }, [filters])

  const handleSeedDummyData = async () => {
    const DUMMY_TICKETS = [
      { subject: 'Login page not loading on Safari', description: 'Customers report login page hangs on Safari. Chrome works fine.', status: 'Open', priority: 'High', type: 'Bug', category: 'Technical', labels: ['browser', 'login'] },
      { subject: 'Export invoices to Excel with date range', description: 'Need ability to export tax invoices with custom date range filter.', status: 'In Progress', priority: 'Medium', type: 'Feature', category: 'Sales', labels: ['export', 'invoice'] },
      { subject: 'Password reset email not received', description: 'User tried forgot password 3 times but did not receive email.', status: 'Resolved', priority: 'Critical', type: 'Incident', category: 'Auth', labels: ['password', 'email'] },
      { subject: 'How to bulk update customer GSTIN?', description: '50+ customers need GSTIN update. Bulk edit option?', status: 'Open', priority: 'Low', type: 'Question', category: 'Sales', labels: ['bulk', 'gstin'] },
      { subject: 'Dashboard showing incorrect revenue', description: 'Dashboard shows ₹2.5L but actual is ₹3.1L for Jan 2026.', status: 'In Progress', priority: 'High', type: 'Bug', category: 'Accounts', labels: ['dashboard', 'revenue'] },
      { subject: 'Push notifications not working on Android', description: 'Push notifications work on iOS but not on Android 13/14.', status: 'Open', priority: 'Medium', type: 'Bug', category: 'Mobile', labels: ['mobile', 'android'] }
    ]
    try {
      toast.info('Creating dummy tickets...')
      let created = 0
      for (const t of DUMMY_TICKETS) {
        const res = await supportTicketService.create({
          subject: t.subject,
          description: t.description,
          status: t.status,
          priority: t.priority,
          type: t.type,
          category: t.category,
          labels: t.labels || []
        })
        if (res.success) created++
      }
      toast.success(`${created} dummy tickets created (ALT+F1)`)
      loadData()
    } catch (e) {
      toast.error('Failed to create dummy tickets')
    }
  }

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.altKey && (e.key === 'F1' || e.keyCode === 112)) {
        e.preventDefault()
        handleSeedDummyData()
      }
    }
    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [ticketsRes, statsRes] = await Promise.all([
        supportTicketService.getAll({
          page: 1,
          limit: 500,
          ...filters,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        }),
        supportTicketService.getStats()
      ])

      if (ticketsRes.success) {
        const tickets = (ticketsRes.data || []).map((t) => ({
          ...t,
          id: t._id
        }))
        setData(tickets)
        if (tickets.length === 0 && !hasShownEmptyRef.current) {
          toast.info('No support tickets found. Create your first ticket!')
          hasShownEmptyRef.current = true
        }
      } else {
        setData([])
        toast.error(ticketsRes.message || 'Failed to load tickets')
      }

      if (statsRes.success && statsRes.data) {
        setStats(statsRes.data)
      }
    } catch (error) {
      console.error('Error loading support tickets:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
        navigate('/login')
      } else {
        toast.error('Failed to load support tickets')
      }
      setData([])
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      accessorKey: 'ticketId',
      header: 'Ticket ID',
      size: 120,
      cell: ({ getValue }) => (
        <span className="fw-medium text-primary">{getValue()}</span>
      )
    },
    {
      accessorKey: 'subject',
      header: 'Subject',
      size: 220,
      cell: ({ getValue }) => (
        <div className="description-cell" style={{ maxWidth: 280 }}>
          <span className="description-text">{getValue() || '—'}</span>
        </div>
      )
    },
    {
      accessorKey: 'type',
      header: 'Type',
      size: 100,
      cell: ({ getValue }) => (
        <span className={`type-badge type-${(getValue() || '').toLowerCase()}`}>
          {getValue() || '—'}
        </span>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      cell: ({ getValue }) => (
        <span className={`status-badge ${(getValue() || '').toLowerCase().replace(/\s/g, '-')}`}>
          {getValue() || '—'}
        </span>
      )
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      size: 100,
      cell: ({ getValue }) => {
        const p = getValue()
        return (
          <span className={`priority-badge priority-${priorityClass(p)}`}>
            {p || '—'}
          </span>
        )
      }
    },
    {
      accessorKey: 'assignee',
      header: 'Assignee',
      size: 140,
      cell: ({ getValue }) => {
        const a = getValue()
        return (
          <span className="text-muted">{a?.name || a?.userName || 'Unassigned'}</span>
        )
      }
    },
    {
      accessorKey: 'customer',
      header: 'Customer',
      size: 140,
      cell: ({ getValue }) => {
        const c = getValue()
        return (
          <span className="text-muted">{c?.customerName || c?.customerCode || '—'}</span>
        )
      }
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      size: 100,
      cell: ({ getValue }) => {
        const d = getValue()
        if (!d) return '—'
        return new Date(d).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        })
      }
    }
  ]

  const handleAddNew = () => navigate('/support/support-tickets/form')
  const handleExport = () => toast.info('Export coming soon')
  const handleEdit = (row) => {
    const id = row._id || row.id
    if (!id) return toast.error('Invalid ticket')
    navigate(`/support/support-tickets/edit/${id}`, { state: { ticketData: row } })
  }
  const handleView = (row) => {
    const id = row._id || row.id
    if (!id) return toast.error('Invalid ticket')
    navigate(`/support/support-tickets/view/${id}`, { state: { ticketData: row } })
  }
  const handleDelete = (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return
    const id = deleteItem._id || deleteItem.id
    try {
      const res = await supportTicketService.delete(id)
      if (res.success) {
        toast.success('Ticket deleted')
        loadData()
      } else toast.error(res.message || 'Failed to delete')
    } catch (e) {
      toast.error('Failed to delete ticket')
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const breadcrumbItems = [
    { label: 'Support', path: '/support' },
    { label: 'Support Tickets', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>
      <div className="page-header">
        <div className="page-title">
          <h1>Support Tickets</h1>
          <p>Manage customer support requests and issues</p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={handleAddNew}>
            <Plus size={18} />
            Add New
          </button>
        </div>
      </div>
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon"><MessageSquareText size={16} /></div>
          </div>
          <div className="metric-value">{stats.totalTickets}</div>
          <div className="metric-title">Total Tickets</div>
        </div>
        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon"><Clock size={16} /></div>
          </div>
          <div className="metric-value">{stats.openTickets}</div>
          <div className="metric-title">Open</div>
        </div>
        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon"><User size={16} /></div>
          </div>
          <div className="metric-value">{stats.inProgressTickets}</div>
          <div className="metric-title">In Progress</div>
        </div>
        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon"><CheckCircle size={16} /></div>
          </div>
          <div className="metric-value">{stats.resolvedTickets}</div>
          <div className="metric-title">Resolved</div>
        </div>
        <div className="metric-card">
          <div className="metric-header">
            <div className="metric-icon"><AlertTriangle size={16} /></div>
          </div>
          <div className="metric-value">{stats.criticalTickets}</div>
          <div className="metric-title">Critical</div>
        </div>
      </div>
      <DataTable
        data={data}
        columns={columns}
        loading={loading}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onExport={handleExport}
        noCard={true}
        pageSize={10}
      />
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteItem(null) }}
        onConfirm={confirmDelete}
        title="Delete Ticket"
        message={`Are you sure you want to delete ticket "${deleteItem?.ticketId}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default SupportTicketList
